import React from "react";
import ReactExport from "react-export-excel";

import moment from 'moment';
import L from '../services/localizationService';

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;

class CTCTimingExcelExport extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            data: null
        }

        this.fontSize = 9;
        this.labelRightStyle = {
            alignment: { 
                horizontal: 'right', 
                wrapText: true 
            }, 
            font: {
                bold: true, 
                sz: this.fontSize
            },
        };

        this.labelStyle = {
            alignment: { 
                horizontal: 'left', 
                wrapText: true 
            }, 
            font: {
                bold: true, 
                sz: this.fontSize
            },
        };

        this.headerStyle = {
            alignment: { 
                horizontal: 'center', 
                wrapText: true 
            }, 
            font: {
                bold: true, 
                sz: this.fontSize
            },
            border: {
                bottom : {
                    style: 'thin', 
                    color: { auto : 1}
                },
                top : {
                    style: 'medium'
                }
            }
        };
        this.numberFormat = '_($* #,##0_);_($* (#,##0);_($* "-"??_);_(@_)';

    }

    buildStyle(additionalSettings) {
        let style = {
            font: {
                sz: this.fontSize
            }
        }

        return {
            ...style,
            ...additionalSettings
        }
    }

    asCurrencyFormulaCell(value, additionalStyles) {
        return { 
            value: value,
            style: this.buildStyle({ numFmt: this.numberFormat, ...additionalStyles})
        };
    }

    asCurrencyCell(value, additionalStyles) {
        return { 
            value: L.parseFloatWithZero(value),
            style: this.buildStyle({ numFmt: this.numberFormat, ...additionalStyles})
        };
    }

    asStringCell(value, additionalStyles) {
        return { 
            value: value,
            style: this.buildStyle(additionalStyles)
        };
    }

    asEmptyCell(additionalStyles) {
        return { 
            value: null,
            style: this.buildStyle(additionalStyles)
        };
    }

    getProjectHeaderCells() {
        let headerCells = [];
        let gapCells = new Array(12).fill(this.asEmptyCell());

        headerCells.push([
            this.asEmptyCell(),
            this.asStringCell('PROJECT:', this.labelStyle),
            this.asStringCell(this.props.data.project.ProjDesc),
        ]);
        headerCells.push([
            this.asEmptyCell(),
            this.asStringCell('PROJECT #:', this.labelStyle),
            this.asStringCell(this.props.data.project.ProjNum.trim()),
            ...gapCells,
            this.asStringCell(this.props.data.applyExchangeRate ? 'MXN RATE:' : '', this.labelRightStyle),
            this.asStringCell(this.props.data.applyExchangeRate ? this.props.data.exchangeRate : ''),
        ]);

        headerCells.push([
            this.asEmptyCell(),
            this.asStringCell('PROJ. MGR:', this.labelStyle),
            this.asStringCell(this.props.data.project.ProjMgr),
            ...gapCells,
            this.asStringCell('FORECAST DATE:', this.labelRightStyle),
            this.asStringCell(this.props.data.forecastDate),
        ]);

        return headerCells;
    }

    getHeaderCells() {
        let headerCells = [];
        let styleWithRightBorder = JSON.parse(JSON.stringify(this.headerStyle));
        styleWithRightBorder.border.right = { style: 'medium' };

        let styleWithLeftBorder = JSON.parse(JSON.stringify(this.headerStyle));
        styleWithLeftBorder.border.left = { style: 'thin' };

        headerCells.push(this.asStringCell('Line Code', this.headerStyle));
        headerCells.push(this.asStringCell('Description', this.headerStyle));
        headerCells.push(this.asStringCell('Estimated Final Cost', this.headerStyle));
        headerCells.push(this.asStringCell('Current WIP', styleWithRightBorder));

        for (let i=0; i < 12; i++) {
            headerCells.push(this.asStringCell(moment().add(i, 'months').format('MMM'), this.headerStyle));
        }

        headerCells.push(this.asStringCell('TOTAL', styleWithLeftBorder));
        headerCells.push(this.asStringCell('BALANCE', styleWithLeftBorder));

        return headerCells;
    }

    getBodyData() {
        let bodyData = [];
        let lineItemCount = this.props.data.projectCostDetailReport.length;
        const tableRowOffset = 6;
        const tableColumnOffset = 'C';
        const tableColumnOffsetAsc = tableColumnOffset.charCodeAt(tableColumnOffset);

        let rightBorderStyleMed = {
            border: {
                right : {
                    style: 'medium', 
                    color: { auto : 1}
                }
            }
        }
        
        let leftBorderStyleMed = {
            border: {
                left : {
                    style: 'thin', 
                    color: { auto : 1}
                }
            }
        }
        
        let boxBorderStyleThin = {
            border: {
                bottom : {
                    style: 'thin'
                },
                top : {
                    style: 'thin'
                },
                right : {
                    style: 'thin'
                },
                left : {
                    style: 'thin'
                },
            }
        }

        // Line item rows
        this.props.data.projectCostDetailReport.forEach((lineItem, idx) => {
            let row = [
                this.asStringCell(lineItem.CostCode),
                this.asStringCell(lineItem.WMS_DerCostCodeDescription),
                this.asCurrencyCell(L.parseFloatWithZero(lineItem.EstimatedFinalCost)),
                this.asCurrencyCell(L.parseFloatWithZero(lineItem.ACost), rightBorderStyleMed)
            ];

            for (let i=0; i < 12; i++) {
                row.push(this.asCurrencyCell(this.props.data.monthsData[idx][i]));
            }

            row.push(this.asCurrencyFormulaCell(`=SUM(E${tableRowOffset+idx}:P${tableRowOffset+idx})`, leftBorderStyleMed));
            row.push(this.asCurrencyFormulaCell(`=C${tableRowOffset+idx}-D${tableRowOffset+idx}-Q${tableRowOffset+idx}`, leftBorderStyleMed));
            
            bodyData.push(row);
        })

        // Empty row
        let emptyRow = [this.asEmptyCell(), this.asEmptyCell(), this.asEmptyCell(), this.asStringCell('', rightBorderStyleMed)];
        for (let i=0; i < 12; i++) {
            emptyRow.push(this.asEmptyCell());
        }
        emptyRow.push(this.asStringCell('', leftBorderStyleMed));

        bodyData.push(emptyRow);

        // Totals
        let totalsRow = [this.asEmptyCell(), this.asEmptyCell()];
        let startRow = tableRowOffset, endRow = tableRowOffset + lineItemCount - 1;

        for (let i=0; i < 16; i++) {
            let columnChar = String.fromCharCode(tableColumnOffsetAsc + i);
            totalsRow.push(this.asCurrencyFormulaCell(`=SUM(${columnChar}${startRow}:${columnChar}${endRow})`, boxBorderStyleThin));
        }

        bodyData.push(totalsRow);

        return bodyData;
    }

    render() {
        if (!this.props.data || this.props.data.loadingData) return null;

        let dataSet = [
            {
                columnwidths: [4.38, 25, 12, 12, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 12, 12],
                columns: new Array(14),
                data: [
                    ...this.getProjectHeaderCells(),
                    this.getHeaderCells(),
                    ...this.getBodyData(),
                ]
            }
        ];

        return (
            <ExcelFile hideElement filename="CTC Timing">
                <ExcelSheet dataSet={dataSet} name="CTC Timing" scale="68" orientation="landscape" />
            </ExcelFile>
        );
    }
}

export default CTCTimingExcelExport;
